/**
 * Production-ready reimagined Document Center component.
 *
 * Renders inside the existing `observability.upload` route when the
 * beta experience is enabled and the user navigates directly (not via
 * upload-intent query params).
 *
 * Strips out: demo mode, DataIndicator badges, links to reimagined-only
 * routes (advisor, etc).
 */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import { LinkTo } from '@ember/routing';
import { VStack } from 'tio-ui/components/layout';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Chip from 'tio-ui/components/chip';
import Drawer from 'tio-ui/components/drawer';
import SectionHeader from 'tio-ui/components/section-header';
import PageHero from 'tio-ui/components/page-hero';
import GlassCard from 'tio-ui/components/glass-card';
import Card from 'tio-ui/components/card';
import ExtractionSummary from 'tio-employee/components/observability/extraction-summary';
import type { ObservableDocumentExtension as ObservableDocumentModel } from 'tio-data/data/resources/observable-document.ext';
import { EXTRACTION_STATES } from 'tio-data/data/resources/observable-document.constants';
import { formatShortDate } from 'tio-ui/format';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import {
  PhFileText,
  PhUploadSimple,
  PhCheckCircle,
  PhWarning,
  PhClock,
  PhEye,
  PhCloudArrowUp,
  PhSparkle,
  PhRobot,
  PhDownloadSimple,
  PhArrowSquareOut,
  PhFolderOpen,
} from 'ember-phosphor-icons';

export interface ReimaginedDocumentCenterModel {
  observableDocuments: ObservableDocumentModel[];
}

interface DocumentCenterSignature {
  Args: {
    model: ReimaginedDocumentCenterModel;
  };
}

interface Document {
  id: string;
  name: string;
  type: string;
  status: 'verified' | 'pending' | 'action-needed';
  uploadDate: string;
  statusLabel: string;
}

const PROVIDER_LABELS: Record<string, string> = {
  'ObservableProvider.NSLDS': 'NSLDS File',
  'ObservableProvider.ACCOUNT_STATEMENT': 'Loan Statement',
  'ObservableProvider.ACCOUNT_TXN_HISTORY': 'Transaction History',
  'ObservableProvider.PROXY': 'Proxy Upload',
};

function mapExtractionStatus(state: keyof typeof EXTRACTION_STATES): Document['status'] {
  const level = EXTRACTION_STATES[state];
  if (!level) return 'pending';
  if (level >= 4) return 'verified'; // PROCESSED or DERELICT
  if (state === 'ExtractionState.FAILED' || state === 'ExtractionState.INVALID')
    return 'action-needed';
  return 'pending'; // UPLOADED, DISPATCHED, PROCESSING
}

function mapExtractionLabel(state: keyof typeof EXTRACTION_STATES): string {
  switch (state) {
    case 'ExtractionState.PROCESSED':
      return 'Processed';
    case 'ExtractionState.DERELICT':
      return 'Processed';
    case 'ExtractionState.FAILED':
      return 'Failed';
    case 'ExtractionState.INVALID':
      return 'Invalid';
    case 'ExtractionState.PROCESSING':
      return 'Processing';
    case 'ExtractionState.DISPATCHED':
      return 'Processing';
    case 'ExtractionState.UPLOADED':
      return 'Uploaded';
    default:
      return 'Unknown';
  }
}

function mapObservableToDocument(doc: ObservableDocumentModel): Document {
  const filename = doc.observableAsset?.filename || 'Unknown Document';
  const provider = doc.provider || '';
  const type = PROVIDER_LABELS[provider] || 'Document';
  const status = mapExtractionStatus(doc.extractionState);
  const statusLabel = mapExtractionLabel(doc.extractionState);
  const uploadDate = doc.createdAt ? formatShortDate(doc.createdAt) : '';

  return {
    id: doc.id ?? '',
    name: filename,
    type,
    status,
    uploadDate,
    statusLabel,
  };
}

export default class ReimaginedDocumentCenter extends Component<DocumentCenterSignature> {
  @service declare reimaginedData: ReimaginedDataService;

  @tracked selectedDocumentId: string | null = null;

  get selectedObservableDocument(): ObservableDocumentModel | undefined {
    if (!this.selectedDocumentId) return undefined;
    return this.args.model.observableDocuments.find((d) => d.id === this.selectedDocumentId);
  }

  get selectedDocument(): Document | undefined {
    if (!this.selectedDocumentId) return undefined;
    return this.documents.find((d) => d.id === this.selectedDocumentId);
  }

  get isViewerOpen() {
    return !!this.selectedDocumentId;
  }

  viewDocument = (docId: string) => {
    this.selectedDocumentId = docId;
  };

  closeViewer = () => {
    this.selectedDocumentId = null;
  };

  get documents(): Document[] {
    return this.args.model.observableDocuments.map(mapObservableToDocument);
  }

  get hasDocuments() {
    return this.documents.length > 0;
  }

  get showLoanUploads() {
    return this.reimaginedData.showStudentLoanRepayment || this.reimaginedData.showPslf;
  }

  get verifiedCount() {
    return this.documents.filter((d) => d.status === 'verified').length;
  }

  get pendingCount() {
    return this.documents.filter((d) => d.status === 'pending').length;
  }

  get actionNeededCount() {
    return this.documents.filter((d) => d.status === 'action-needed').length;
  }

  <template>
    {{! template-lint-disable no-bare-strings }}
    <VStack class="gap-8 pb-12">
      {{! Hero Section }}
      <PageHero @decoration="circles" @rounded="2xl">
        <div class="tio-row mb-4">
          <div class="tio-document-center-hero-icon">
            <PhFileText class="h-7 w-7 tio-fg-inverse" />
          </div>
          <div>
            <Heading @level={{2}} class="tio-fg-inverse font-bold">
              Document Center
            </Heading>
            <Text @size="sm" class="tio-fg-on-brand">
              Upload, verify, and manage your financial documents
            </Text>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <GlassCard
            @label="Verified"
            @value={{this.verifiedCount}}
            @accent="default"
            @subtext="documents approved"
          />
          <GlassCard
            @label="Pending"
            @value={{this.pendingCount}}
            @accent="default"
            @subtext="under review"
          />
          <GlassCard
            @label="Action Needed"
            @value={{this.actionNeededCount}}
            @accent="default"
            @subtext="require attention"
          />
        </div>
      </PageHero>

      {{! Document List }}
      <SectionHeader
        @icon={{PhFileText}}
        @title="Your Documents"
        @subtitle="All uploaded and verified documents"
        @iconClass="tio-fg-brand-accent"
      />
      {{#if this.documents.length}}
        <div class="space-y-3">
          {{#each this.documents as |doc|}}
            <Card @padding="sm" class="tio-row-between hover:shadow-md transition-shadow">
              <div class="tio-row-loose">
                <div
                  class="tio-row-center h-12 w-12 rounded-lg
                    {{if
                      (eq doc.status 'verified')
                      'tio-bg-success-soft'
                      (if (eq doc.status 'pending') 'tio-bg-warning-soft' 'tio-bg-danger-soft')
                    }}"
                >
                  {{#if (eq doc.status "verified")}}
                    <PhCheckCircle class="tio-icon-lg tio-fg-success" />
                  {{else if (eq doc.status "pending")}}
                    <PhClock class="tio-icon-lg tio-fg-warning" />
                  {{else}}
                    <PhWarning class="tio-icon-lg tio-fg-danger" />
                  {{/if}}
                </div>
                <div>
                  <div class="font-medium tio-fg-primary">{{doc.name}}</div>
                  <div class="tio-row-tight mt-1">
                    <Text @size="xs" class="tio-fg-muted">{{doc.type}}</Text>
                    <span class="tio-fg-faint">·</span>
                    <Text @size="xs" class="tio-fg-muted">{{doc.uploadDate}}</Text>
                  </div>
                </div>
              </div>
              <div class="tio-row">
                <Chip
                  @intent={{if
                    (eq doc.status "verified")
                    "success"
                    (if (eq doc.status "pending") "warning" "danger")
                  }}
                >
                  {{doc.statusLabel}}
                </Chip>
                <button
                  type="button"
                  class="tio-fg-disabled hover:tio-fg-secondary p-2"
                  {{on "click" (fn this.viewDocument doc.id)}}
                >
                  <PhEye class="tio-icon-md" />
                  <span class="sr-only">
                    View document
                    {{doc.name}}
                  </span>
                </button>
              </div>
            </Card>
          {{/each}}
        </div>
      {{else}}
        <div
          class="tio-bg-surface rounded-xl border border-dashed tio-border-default p-12 text-center"
        >
          <div class="tio-row-center h-16 w-16 rounded-full tio-bg-brand-soft mx-auto mb-4">
            <PhFolderOpen class="tio-icon-xl tio-fg-brand-accent" />
          </div>
          <Heading @level={{3}} class="mb-2">
            No Documents Yet
          </Heading>
          <Text @size="sm" class="tio-fg-muted mb-6 max-w-md mx-auto">
            Upload your loan statements, NSLDS file, or other documents to unlock employer
            contributions and track your progress.
          </Text>
          {{#if this.showLoanUploads}}
            <LinkTo
              @route="authenticated.observability.upload"
              @query={{hash
                source="reimagined-documents"
                providers="nslds,statement"
                returnTo="authenticated.observability.upload"
              }}
              class="tio-inline-row gap-2 rounded-lg tio-bg-brand hover:tio-bg-brand-hover px-5 py-2.5 tio-text-sm-medium tio-fg-inverse shadow-sm transition-colors"
            >
              <PhUploadSimple class="tio-icon-sm" />
              Upload Documents
            </LinkTo>
          {{/if}}
        </div>
      {{/if}}

      {{! Upload Methods }}
      <SectionHeader
        @icon={{PhCloudArrowUp}}
        @title="Upload Methods"
        @subtitle="Choose how to share your documents"
        @iconClass="tio-fg-info"
      />
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card @padding="lg" class="text-center">
          <div class="tio-row-center h-14 w-14 rounded-xl tio-bg-brand-soft mx-auto mb-4">
            <PhCloudArrowUp class="h-7 w-7 tio-fg-brand" />
          </div>
          <Heading @level={{4}} class="mb-2">File Upload</Heading>
          <Text @size="sm" class="tio-fg-secondary mb-4">
            Drag and drop or browse to upload PDF, JPG, or PNG documents.
          </Text>
          <LinkTo
            @route="authenticated.observability.upload"
            @query={{hash
              source="reimagined-documents"
              providers="nslds,statement"
              returnTo="authenticated.observability.upload"
            }}
            class="block w-full rounded-md border tio-border-brand tio-bg-surface px-4 py-2 text-center tio-text-sm-medium tio-fg-brand shadow-sm hover:tio-bg-brand-wash focus:outline-none focus-visible:ring-2 focus-visible:tio-ring-brand focus-visible:ring-offset-2"
          >
            Upload Files
          </LinkTo>
        </Card>

        {{#if this.showLoanUploads}}
          <Card @padding="lg" class="text-center">
            <div class="tio-row-center h-14 w-14 rounded-xl tio-bg-info-soft mx-auto mb-4">
              <PhSparkle class="h-7 w-7 tio-fg-info" />
            </div>
            <Heading @level={{4}} class="mb-2">Upload Documents</Heading>
            <Text @size="sm" class="tio-fg-secondary mb-4">
              Upload your loan statements or NSLDS file for automatic data verification.
            </Text>
            <LinkTo
              @route="authenticated.observability.upload"
              @query={{hash
                source="reimagined-documents"
                providers="nslds,statement"
                returnTo="authenticated.observability.upload"
              }}
              class="block w-full rounded-md border tio-border-info tio-bg-surface px-4 py-2 text-center tio-text-sm-medium tio-fg-info shadow-sm hover:tio-bg-info-soft focus:outline-none focus-visible:ring-2 focus-visible:tio-ring-info focus-visible:ring-offset-2"
            >
              Upload Documents
            </LinkTo>
          </Card>
        {{/if}}

        <Card @padding="lg" class="text-center">
          <div class="tio-row-center h-14 w-14 rounded-xl tio-bg-info-soft mx-auto mb-4">
            <PhRobot class="h-7 w-7 tio-fg-info" />
          </div>
          <Heading @level={{4}} class="mb-2">AI-Assisted</Heading>
          <Text @size="sm" class="tio-fg-secondary mb-4">
            Our AI reads and extracts data from your documents automatically.
          </Text>
          <Chip @intent="primary">Coming Soon</Chip>
        </Card>
      </div>

      {{! Document Viewer Drawer }}
      <Drawer @isOpen={{this.isViewerOpen}} @onClose={{this.closeViewer}} @size="lg">
        <:header>
          <div class="tio-row">
            <div class="p-2 rounded-lg tio-bg-brand-soft">
              <PhFileText class="tio-icon-md tio-fg-brand" />
            </div>
            <div class="tio-fill-min">
              <Heading @level={{3}} class="truncate">
                {{#if this.selectedDocument}}
                  {{this.selectedDocument.name}}
                {{else}}
                  Document Details
                {{/if}}
              </Heading>
              {{#if this.selectedDocument}}
                <Text @size="sm" class="tio-fg-muted">{{this.selectedDocument.type}}</Text>
              {{/if}}
            </div>
            {{#if this.selectedDocument}}
              <Chip
                @intent={{if
                  (eq this.selectedDocument.status "verified")
                  "success"
                  (if (eq this.selectedDocument.status "pending") "warning" "danger")
                }}
              >
                {{this.selectedDocument.statusLabel}}
              </Chip>
            {{/if}}
          </div>
        </:header>
        <:body>
          {{#if this.selectedDocument}}
            <div class="space-y-4">
              <div class="grid grid-cols-2 gap-4">
                <div>
                  <Text @size="xs" class="tio-fg-muted uppercase tracking-wide mb-1">Upload Date</Text>
                  <Text @size="sm" @weight="medium">{{this.selectedDocument.uploadDate}}</Text>
                </div>
                <div>
                  <Text @size="xs" class="tio-fg-muted uppercase tracking-wide mb-1">Document Type</Text>
                  <Text @size="sm" @weight="medium">{{this.selectedDocument.type}}</Text>
                </div>
                <div>
                  <Text @size="xs" class="tio-fg-muted uppercase tracking-wide mb-1">Status</Text>
                  <Text @size="sm" @weight="medium">{{this.selectedDocument.statusLabel}}</Text>
                </div>
                <div>
                  <Text @size="xs" class="tio-fg-muted uppercase tracking-wide mb-1">Document ID</Text>
                  <Text
                    @size="sm"
                    class="font-mono tio-fg-muted"
                  >{{this.selectedDocument.id}}</Text>
                </div>
              </div>

              {{! Extraction summary for real documents }}
              {{#if this.selectedObservableDocument}}
                <div class="border-t tio-border-default pt-4 mt-4">
                  <Heading @level={{4}} @size="sm" class="mb-3">
                    Extracted Data
                  </Heading>
                  {{#if (eq this.selectedDocument.status "verified")}}
                    <ExtractionSummary
                      @document={{this.selectedObservableDocument}}
                      class="w-full"
                    />
                  {{else if (eq this.selectedDocument.status "pending")}}
                    <div class="tio-row p-4 tio-bg-warning-soft rounded-lg">
                      <PhClock class="tio-icon-md tio-fg-warning shrink-0" />
                      <Text @size="sm" class="tio-fg-warning-body">
                        This document is being processed. Data will appear here once extraction is
                        complete.
                      </Text>
                    </div>
                  {{else}}
                    <div class="tio-row p-4 tio-bg-danger-soft rounded-lg">
                      <PhWarning class="tio-icon-md tio-fg-danger shrink-0" />
                      <Text @size="sm" class="tio-fg-danger-body">
                        We couldn't extract data from this document. Please try uploading again or
                        contact support.
                      </Text>
                    </div>
                  {{/if}}
                </div>
              {{/if}}
            </div>
          {{/if}}
        </:body>
        <:footer>
          <div class="tio-row">
            {{#if this.selectedObservableDocument}}
              <a
                href={{this.selectedObservableDocument.observableAsset.signedUrl}}
                target="_blank"
                rel="noopener noreferrer"
                class="tio-inline-row gap-2 px-4 py-2 tio-bg-brand hover:tio-bg-brand-hover tio-fg-inverse rounded-lg transition-colors tio-text-sm-medium"
              >
                <PhDownloadSimple @size={{16}} />
                Download Original
                <PhArrowSquareOut @size={{14}} />
              </a>
            {{/if}}
            <button
              type="button"
              class="px-4 py-2 tio-fg-secondary border tio-border-default rounded-lg hover:tio-bg-sunken transition-colors tio-text-sm-medium"
              {{on "click" this.closeViewer}}
            >
              Close
            </button>
          </div>
        </:footer>
      </Drawer>
    </VStack>
  </template>
}
